import { useEffect, useMemo, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';

interface VoiceInputButtonProps {
	isRecording: boolean;
	isTranscribing: boolean;
	onToggle: () => void;
	disabled?: boolean;
	error: string | null;
	audioLevel?: number;
	recordingDuration?: number;
}

const ERROR_DISPLAY_DURATION_MS = 4000;
const WAVEFORM_BAR_COUNT = 24;

function formatDuration(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}:${s.toString().padStart(2, '0')}`;
}

function WaveformVisualizer({ audioLevel }: { audioLevel: number }) {
	const bars = useMemo(() => {
		return Array.from({ length: WAVEFORM_BAR_COUNT }, (_, i) => {
			const position = i / WAVEFORM_BAR_COUNT;
			const centerDistance = Math.abs(position - 0.5) * 2;
			const baseHeight = 0.15 + (1 - centerDistance) * 0.3;
			return baseHeight;
		});
	}, []);

	const now = Date.now();

	return (
		<div className="flex items-center gap-[2px] h-5">
			{bars.map((baseHeight, i) => {
				const noise = Math.sin(i * 1.7 + now / 200) * 0.15;
				const height = Math.max(0.1, Math.min(1, baseHeight + audioLevel * (0.6 + noise)));
				return (
					<div
						key={i}
						className="w-[2px] rounded-full bg-red-400 transition-all duration-75"
						style={{ height: `${height * 100}%` }}
					/>
				);
			})}
		</div>
	);
}

export function VoiceInputButton({
	isRecording,
	isTranscribing,
	onToggle,
	disabled = false,
	error,
	audioLevel = 0,
	recordingDuration = 0,
}: VoiceInputButtonProps) {
	const [showError, setShowError] = useState(false);

	useEffect(() => {
		if (error) {
			setShowError(true);
			const timer = setTimeout(() => setShowError(false), ERROR_DISPLAY_DURATION_MS);
			return () => clearTimeout(timer);
		}
		setShowError(false);
	}, [error]);

	// Force re-render for waveform animation while recording
	const [, setTick] = useState(0);
	useEffect(() => {
		if (!isRecording) return;
		const id = setInterval(() => setTick((t) => t + 1), 75);
		return () => clearInterval(id);
	}, [isRecording]);

	if (isRecording) {
		return (
			<div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-red-500/10 border border-red-500/20">
				<div className="flex items-center gap-1.5">
					<span className="size-2 rounded-full bg-red-500 animate-pulse" />
					<span className="text-xs font-mono text-red-400 tabular-nums min-w-[28px]">
						{formatDuration(recordingDuration)}
					</span>
				</div>
				<WaveformVisualizer audioLevel={audioLevel} />
				<button
					type="button"
					onClick={onToggle}
					className="p-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 hover:text-red-300 transition-colors"
					aria-label="Stop recording"
					title="Stop recording"
				>
					<Square className="size-3.5" strokeWidth={2.5} fill="currentColor" />
				</button>
			</div>
		);
	}

	const isDisabled = disabled || isTranscribing;

	return (
		<div className="relative">
			<button
				type="button"
				onClick={onToggle}
				disabled={isDisabled}
				className="p-1.5 rounded-md transition-all duration-200 text-text-tertiary hover:text-text-secondary hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
				aria-label={isTranscribing ? 'Transcribing...' : 'Voice input'}
				title={isTranscribing ? 'Transcribing...' : 'Voice input'}
			>
				{isTranscribing ? (
					<Loader2 className="size-4 animate-spin" />
				) : (
					<Mic className="size-4" />
				)}
			</button>
			{showError && error && (
				<span className="absolute -top-8 right-0 px-2 py-1 bg-bg-1 border border-border-primary rounded text-xs text-red-400 whitespace-nowrap pointer-events-none z-10">
					{error}
				</span>
			)}
		</div>
	);
}
